import React, { useState } from 'react';
import { Check, X, AlertCircle, Circle, ChevronDown, ChevronUp, Info } from 'lucide-react';

/**
 * SecurityServicesTable - Main table/matrix component for Security Services
 * Displays services vs features matrix with responsive design
 */
const SecurityServicesTable = ({ services, features, featureServices, viewMode }) => {
  const [expandedService, setExpandedService] = useState(null);

  // Helper function to get appropriate icon and styling for feature values
  const getFeatureDisplay = (value) => {
    switch (value?.toLowerCase()) {
      case 'yes':
        return {
          icon: <Check className="w-4 h-4" />,
          bgColor: 'bg-green-500',
          textColor: 'text-white',
          label: 'Available'
        };
      case 'no':
        return {
          icon: <X className="w-4 h-4" />,
          bgColor: 'bg-red-500',
          textColor: 'text-white',
          label: 'Not Available'
        };
      case 'partial':
        return {
          icon: <AlertCircle className="w-4 h-4" />,
          bgColor: 'bg-amber-500',
          textColor: 'text-white',
          label: 'Limited Support'
        };
      default:
        return {
          icon: <Circle className="w-4 h-4" />,
          bgColor: 'bg-gray-400',
          textColor: 'text-white',
          label: 'Not Applicable'
        };
    }
  };

  // Cards View Component
  const CardsView = () => (
    <div 
      className="h-full overflow-y-auto"
      style={{
        scrollSnapType: 'none',
        overscrollBehavior: 'contain'
      }}
    >
      <div className="p-6">
        <div className="grid gap-6 lg:grid-cols-2 xl:grid-cols-3">
          {services.map((service) => (
            <div key={service.id} className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
              <div className="p-6">
                {/* Service Header */}
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">{service.name}</h3>
                    <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
                      {service.category}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">{service.description}</p>
                </div>

                {/* Features List */}
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Capabilities</h4>
                  {features.map((feature) => {
                    const featureValue = featureServices[feature.id]?.[service.id];
                    const display = getFeatureDisplay(featureValue);
                    
                    return (
                      <div key={feature.id} className="flex items-center justify-between py-1">
                        <span className="text-sm text-gray-600 truncate pr-2">{feature.name}</span>
                        <div className={`flex items-center justify-center w-6 h-6 rounded-full ${display.bgColor} ${display.textColor}`}>
                          {display.icon}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // Matrix View Component - Transposed: Services as columns, Features as rows
  const MatrixView = () => (
    <div className="h-full flex flex-col">
      <div 
        className="flex-1 overflow-auto"
        style={{
          scrollSnapType: 'none',
          overscrollBehavior: 'contain'
        }}
      >
        <table className="min-w-full bg-white">
          {/* Table Header */}
          <thead className="bg-gradient-to-r from-blue-600 to-blue-700 text-white sticky top-0 z-20">
            <tr>
              <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider sticky left-0 bg-blue-600 z-30 min-w-[300px]">
                Feature / Service
              </th>
              {services.map((service) => (
                <th
                  key={service.id}
                  className="px-3 py-4 text-center text-sm font-semibold uppercase tracking-wider min-w-[150px]"
                  title={service.description}
                >
                  <div className="flex flex-col items-center space-y-1">
                    <span className="text-xs leading-tight text-center">{service.name}</span>
                    <Info className="w-3 h-3 opacity-60" />
                  </div>
                </th>
              ))}
            </tr>
          </thead>

          {/* Table Body */}
          <tbody className="divide-y divide-gray-200">
            {features.map((feature, featureIndex) => (
              <tr
                key={feature.id}
                className={`hover:bg-gray-50 transition-colors ${
                  featureIndex % 2 === 0 ? 'bg-white' : 'bg-gray-25'
                }`}
              >
                {/* Feature Name Column */}
                <td className="px-6 py-4 sticky left-0 bg-white z-10 border-r border-gray-200 min-w-[300px]">
                  <div className="flex flex-col">
                    <div className="font-medium text-gray-900 text-sm">{feature.name}</div>
                    <div className="text-xs text-gray-500 mt-1">{feature.category}</div>
                  </div>
                </td>

                {/* Service Columns */}
                {services.map((service) => {
                  const featureValue = featureServices[feature.id]?.[service.id];
                  const display = getFeatureDisplay(featureValue);

                  return (
                    <td
                      key={`${feature.id}-${service.id}`}
                      className="px-3 py-4 text-center min-w-[150px]"
                    >
                      <div className="flex flex-col items-center space-y-1">
                        <div
                          className={`flex items-center justify-center w-8 h-8 rounded-full ${display.bgColor} ${display.textColor} shadow-sm`}
                          title={`${service.name}: ${display.label}`}
                        >
                          {display.icon}
                        </div>
                        <span className="text-xs text-gray-600 font-medium">
                          {featureValue || 'N/A'}
                        </span>
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Legend */}
      <div className="bg-gray-50 border-t border-gray-200 px-6 py-4 flex-shrink-0">
        <div className="flex flex-wrap items-center justify-center gap-6">
          <h4 className="text-sm font-medium text-gray-700 mr-4">Legend:</h4>
          
          <div className="flex items-center space-x-2">
            <div className="flex items-center justify-center w-6 h-6 rounded-full bg-green-500 text-white">
              <Check className="w-4 h-4" />
            </div>
            <span className="text-sm text-gray-600">Available</span>
          </div>
          
          <div className="flex items-center space-x-2">
            <div className="flex items-center justify-center w-6 h-6 rounded-full bg-amber-500 text-white">
              <AlertCircle className="w-4 h-4" />
            </div>
            <span className="text-sm text-gray-600">Limited Support</span>
          </div>
          
          <div className="flex items-center space-x-2">
            <div className="flex items-center justify-center w-6 h-6 rounded-full bg-red-500 text-white">
              <X className="w-4 h-4" />
            </div>
            <span className="text-sm text-gray-600">Not Available</span>
          </div>
          
          <div className="flex items-center space-x-2">
            <div className="flex items-center justify-center w-6 h-6 rounded-full bg-gray-400 text-white">
              <Circle className="w-4 h-4" />
            </div>
            <span className="text-sm text-gray-600">Not Applicable</span>
          </div>
        </div>
      </div>
    </div>
  );

  // Empty state
  if (services.length === 0 || features.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-gray-500">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
            <Info className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Results Found</h3>
          <p className="text-gray-500">
            {services.length === 0 ? 'No services match your current filters.' : 'No features match your current filters.'}
          </p>
        </div>
      </div>
    );
  }

  return viewMode === 'cards' ? <CardsView /> : <MatrixView />;
};

export default React.memo(SecurityServicesTable); 